import { QueryResult } from "@apollo/client";
import { captureException } from "@sentry/react";
import {
  Exact,
  useGetExternalTenantsLazyQuery,
  GetExternalTenantsQuery,
} from "graphql/_Types";
import { getEnvVar, ExternalRegion } from "ideas.env";
import { compact, isNull, isUndefined } from "lodash";
import { useState, useEffect, useMemo } from "react";
import { TenantType } from "types/constants";

type TExternalTenantAndRegion = {
  tenants: QueryResult<
    GetExternalTenantsQuery,
    Exact<{
      [key: string]: never;
    }>
  >;
  region: ExternalRegion;
};

export type TExternalTenant = {
  region: ExternalRegion;
  id: number;
  key: string;
  name: string;
  tenantType: TenantType;
};

export const useGetExternalTenants = () => {
  const [getExternalTenants] = useGetExternalTenantsLazyQuery({
    onError: (err) => captureException(err),
    fetchPolicy: "no-cache",
  });
  const [results, setResults] = useState<TExternalTenantAndRegion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    const getAllExternalTenants = async () => {
      try {
        const promises = getEnvVar("EXTERNAL_REGIONS").map(async (region) => ({
          region,
          tenants: await getExternalTenants({
            context: {
              uri: region.URL_GRAPHQL,
              queryDeduplication: false,
            },
          }),
        }));
        const results = await Promise.all(promises);
        setResults(results);
      } catch (error) {
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };
    void getAllExternalTenants();
  }, [getExternalTenants]);

  const externalTenants: TExternalTenant[] = useMemo(
    () =>
      compact(
        results.flatMap(({ tenants, region }) =>
          tenants.data?.currentUser?.memberships.nodes.map((node) => {
            const tenant = node.tenant;
            if (isUndefined(region) || isNull(tenant)) {
              return undefined;
            }
            return {
              ...tenant,
              region,
            };
          }),
        ),
      ),
    [results],
  );

  return { externalTenants, loading, error };
};
